import React from 'react';
import { Message } from '@mentimeter/ragnar-form';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { useTranslations } from 'next-intl';
import { Link } from '@mentimeter/ragnar-ui/link';
import {
  ErrorCodes,
  extractErrorMessage,
  extractMetaData,
  type ClientAuthError,
  type ServerAuthError,
} from '../../clients/userApi/errors';
import { applyQueryToPath } from '../../utils/queryString';
import { EXPOSED_CREDENTIALS_HELP_ARTICLE_URL } from '../../clientErrorMessages';
import { ExposedCredentialsLoginError } from './ExposedCredentialsLoginError';

interface Props extends BoxT {
  error: ClientAuthError | ServerAuthError;
  continueUrl: string;
}

const getSeverity = (code: string, type: string) => {
  switch (type) {
    case 'signup':
    case 'login':
      return 'negative';
    case 'social':
      return 'info';
  }
  switch (code) {
    case ErrorCodes.PASSWORD_ATTEMPT_DENIED:
    case ErrorCodes.WRONG_PASSWORD:
    case ErrorCodes.NOT_FOUND:
    case ErrorCodes.SUSPENDED_USER:
    case ErrorCodes.SERVICE_UNAVAILABLE:
    case ErrorCodes.EMAIL_NOT_UNIQUE:
    case ErrorCodes.EXPOSED_CREDENTIALS:
      return 'negative';
    case ErrorCodes.RESTRICTED_DOMAIN:
    case ErrorCodes.SAML_LOGIN_ENABLED:
      return 'info';
    default:
      return 'negative';
  }
};

const CustomLink = (href: string) => (children: React.ReactNode) => (
  <Link href={applyQueryToPath('/auth/login')}>{children}</Link>
);

const ExposedCredentialsLink = (children: React.ReactNode) => (
  <Link
    href={EXPOSED_CREDENTIALS_HELP_ARTICLE_URL}
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </Link>
);

export const AuthErrorMessage = ({ error, continueUrl, ...props }: Props) => {
  const t = useTranslations('authentication.authentication_error');

  if (error.code === ErrorCodes.EXPOSED_CREDENTIALS && error.type === 'login') {
    return <ExposedCredentialsLoginError />;
  }

  const severity = getSeverity(error.code, error.type);

  let children: React.ReactNode = null;

  switch (error.code) {
    case ErrorCodes.SOCIAL_LOGIN_ENABLED:
      children = t('social_login_enabled_message');
      break;
    case ErrorCodes.PASSWORD_ATTEMPT_DENIED:
      children = t('too_many_attempts_message');
      break;

    case ErrorCodes.WRONG_PASSWORD:
      children = t('wrong_password_message');
      break;
    case ErrorCodes.NOT_FOUND:
      children = t('incorrect_email_message');
      break;

    case ErrorCodes.SUSPENDED_USER:
      {
        const message = extractErrorMessage(error);
        children = message || t('suspended_user_message');
      }
      break;

    case ErrorCodes.SERVICE_UNAVAILABLE: {
      children =
        error.type === 'signup'
          ? t('sign_up_service_unavailable_message')
          : t('log_in_service_unavailable_message');
      break;
    }
    case ErrorCodes.EMAIL_NOT_UNIQUE:
      children = t.rich('already_in_use_message', {
        Link: CustomLink('/auth/login'),
      });
      break;

    case ErrorCodes.EXPOSED_CREDENTIALS:
      children = t.rich('exposed_credentials_learn_more_message', {
        Link: ExposedCredentialsLink,
      });
      break;
    case ErrorCodes.RESTRICTED_DOMAIN: {
      const metaData = extractMetaData(error);
      let loginUrl = metaData?.loginUrl;
      if (continueUrl) {
        loginUrl += `?continue=${continueUrl}`;
      }

      children = t.rich('sign_up_sso_message', {
        Link: CustomLink(loginUrl),
      });
      break;
    }

    case ErrorCodes.SAML_LOGIN_ENABLED: {
      const metaData = extractMetaData(error);
      let loginUrl = metaData?.loginUrl;
      if (continueUrl) {
        loginUrl += `?continue=${continueUrl}`;
      }
      children = t.rich('log_in_sso_message', {
        Link: CustomLink(loginUrl),
      });
    }
  }

  /**
   * If the children is still null, we can use the default message based on the error type.
   */
  if (!children) {
    switch (error.type) {
      case 'signup':
        children = t('sign_up_failed_message');
        break;
      case 'login':
        children = t('log_in_failed_message');
        break;
      case 'social':
        children = t('something_went_wrong_try_another_login_message');
        break;
    }
  }

  if (!children) return null;

  return (
    <Message severity={severity} {...props}>
      {children}
    </Message>
  );
};
